import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, Columns3, Plus, ChevronDown, Pin, PinOff, Share2, Trash2, Save, Check } from 'lucide-react';
import { useViews, useCreateView, useUpdateView, useDeleteView } from '../../hooks/useViews.js';
import { useFeatureFlag } from '../../lib/posthog.js';
import { toast } from '../../lib/toast.js';
import type { SavedView, ViewFilter, ViewSort, ViewEntityType, ViewLayout } from '@mayksel/types';
import type { FieldDef } from './FilterPanel.js';
import type { ColumnDef } from './ColumnPicker.js';
import FilterPanel from './FilterPanel.js';
import ColumnPicker from './ColumnPicker.js';

interface Props {
  entityType: ViewEntityType;
  /** All possible columns for this entity */
  columnDefs: ColumnDef[];
  /** Filterable fields */
  fieldDefs: FieldDef[];
  /** Callback when filters/columns/sort/layout change */
  onViewChange: (state: ViewState) => void;
}

export interface ViewState {
  filters: ViewFilter[];
  columns: string[];
  sort: ViewSort | null;
  layout: ViewLayout;
}

export default function ViewBar({ entityType, columnDefs, fieldDefs, onViewChange }: Props) {
  const { t } = useTranslation('views');
  const viewsEnabled = useFeatureFlag('views');
  const { data: allViews = [] } = useViews(entityType);
  const createView = useCreateView();
  const deleteView = useDeleteView();

  // Active view state
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [localFilters, setLocalFilters] = useState<ViewFilter[]>([]);
  const [localColumns, setLocalColumns] = useState<string[]>(columnDefs.map((c) => c.key));
  const [localSort, setLocalSort] = useState<ViewSort | null>(null);
  const [localLayout, setLocalLayout] = useState<ViewLayout>('table');

  // Popover state
  const [showFilters, setShowFilters] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [showViewMenu, setShowViewMenu] = useState(false);
  const [showNewViewPopover, setShowNewViewPopover] = useState(false);
  const [newViewName, setNewViewName] = useState('');

  const filterRef = useRef<HTMLDivElement>(null);
  const columnRef = useRef<HTMLDivElement>(null);
  const viewMenuRef = useRef<HTMLDivElement>(null);
  const newViewRef = useRef<HTMLDivElement>(null);

  const activeView = allViews.find((v) => v.id === activeViewId) ?? null;
  const isDirty = activeView ? (
    JSON.stringify(localFilters) !== JSON.stringify(activeView.filters) ||
    JSON.stringify(localColumns) !== JSON.stringify(activeView.columns) ||
    JSON.stringify(localSort) !== JSON.stringify(activeView.sort)
  ) : localFilters.length > 0;

  const updateActiveView = useUpdateView(activeViewId ?? '');

  // Load from active view when it changes
  useEffect(() => {
    if (activeView) {
      setLocalFilters(activeView.filters);
      setLocalColumns(activeView.columns.length > 0 ? activeView.columns : columnDefs.map((c) => c.key));
      setLocalSort(activeView.sort);
      setLocalLayout(activeView.layout);
    }
  }, [activeViewId]); // intentionally only re-run when activeViewId changes

  // Notify parent on state change
  useEffect(() => {
    onViewChange({ filters: localFilters, columns: localColumns, sort: localSort, layout: localLayout });
  }, [localFilters, localColumns, localSort, localLayout]); // intentionally omits onViewChange (stable callback)

  // Close popovers on outside click
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) setShowFilters(false);
      if (columnRef.current && !columnRef.current.contains(e.target as Node)) setShowColumns(false);
      if (viewMenuRef.current && !viewMenuRef.current.contains(e.target as Node)) setShowViewMenu(false);
      if (newViewRef.current && !newViewRef.current.contains(e.target as Node)) setShowNewViewPopover(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  function activateView(view: SavedView) {
    setActiveViewId(view.id);
    setShowViewMenu(false);
  }

  function clearActiveView() {
    setActiveViewId(null);
    setLocalFilters([]);
    setLocalColumns(columnDefs.map((c) => c.key));
    setLocalSort(null);
    setLocalLayout('table');
    setShowViewMenu(false);
  }

  function saveCurrentView() {
    if (!activeViewId) return;
    updateActiveView.mutate(
      { filters: localFilters, columns: localColumns, sort: localSort },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: () => toast.error(t('errors.save_failed')),
      },
    );
  }

  function createNewView() {
    const name = newViewName.trim() || t('default_name');
    createView.mutate(
      {
        entityType,
        name,
        layout: localLayout,
        filters: localFilters,
        columns: localColumns,
        sort: localSort,
        groupBy: null,
        isShared: false,
        isPinned: false,
      },
      {
        onSuccess: (view) => {
          setActiveViewId(view.id);
          setNewViewName('');
          setShowNewViewPopover(false);
          toast.success(t('saved'));
        },
        onError: () => toast.error(t('errors.save_failed')),
      },
    );
  }

  function togglePin(view: SavedView) {
    useUpdateView(view.id);
  }

  function handleDeleteView(viewId: string) {
    if (!confirm(t('delete_confirm'))) return;
    deleteView.mutate(viewId, {
      onSuccess: () => {
        if (activeViewId === viewId) clearActiveView();
        toast.success(t('delete_view'));
      },
      onError: () => toast.error(t('errors.delete_failed')),
    });
  }

  const pinnedViews = allViews.filter((v) => v.isPinned);
  const activeCount = localFilters.length;

  if (!viewsEnabled) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>

      {/* Views dropdown */}
      <div ref={viewMenuRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setShowViewMenu((v) => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
            padding: '4px var(--space-3)',
            background: activeView ? 'var(--color-brand-pale)' : 'var(--color-bg-subtle)',
            border: `1px solid ${activeView ? 'var(--color-brand)' : 'var(--color-border)'}`,
            borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
            color: activeView ? 'var(--color-brand)' : 'var(--color-text-secondary)',
            cursor: 'pointer',
          }}
        >
          {activeView ? activeView.name : t('title')}
          <ChevronDown size={12} />
        </button>

        {showViewMenu && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0,
            background: 'var(--color-bg)', border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-md)',
            zIndex: 100, minWidth: 220, padding: 'var(--space-1)',
          }}>
            {/* All records */}
            <ViewMenuItem
              label="All records"
              isActive={!activeViewId}
              onClick={clearActiveView}
            />

            {allViews.length > 0 && (
              <div style={{ borderTop: '1px solid var(--color-border)', margin: 'var(--space-1) 0' }} />
            )}

            {/* Saved views */}
            {allViews.map((view) => (
              <div key={view.id} style={{ display: 'flex', alignItems: 'center' }}>
                <button
                  onClick={() => activateView(view)}
                  style={{
                    flex: 1, padding: 'var(--space-2) var(--space-3)',
                    background: activeViewId === view.id ? 'var(--color-brand-pale)' : 'none',
                    border: 'none', cursor: 'pointer', textAlign: 'left',
                    fontSize: 'var(--text-sm)',
                    color: activeViewId === view.id ? 'var(--color-brand)' : 'var(--color-text)',
                    borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                  }}
                >
                  {view.isPinned && <Pin size={12} style={{ flexShrink: 0 }} />}
                  {view.name}
                  {activeViewId === view.id && <Check size={12} style={{ marginLeft: 'auto' }} />}
                </button>
                <button
                  onClick={() => handleDeleteView(view.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 'var(--space-2)', color: 'var(--color-text-muted)' }}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}

            <div style={{ borderTop: '1px solid var(--color-border)', margin: 'var(--space-1) 0' }} />

            {/* Save as new view */}
            <div ref={newViewRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowNewViewPopover((v) => !v)}
                style={{
                  width: '100%', padding: 'var(--space-2) var(--space-3)',
                  background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                  fontSize: 'var(--text-sm)', color: 'var(--color-brand)',
                  display: 'flex', alignItems: 'center', gap: 'var(--space-2)', borderRadius: 'var(--radius-sm)',
                }}
              >
                <Plus size={14} /> {t('new_view')}
              </button>

              {showNewViewPopover && (
                <div style={{
                  position: 'absolute', left: 'calc(100% + 4px)', top: 0,
                  background: 'var(--color-bg)', border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)',
                  padding: 'var(--space-3)', zIndex: 101, width: 240,
                }}>
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-2)' }}>
                    {t('save_view')}
                  </p>
                  <input
                    autoFocus
                    value={newViewName}
                    onChange={(e) => setNewViewName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') createNewView(); }}
                    placeholder={t('default_name')}
                    style={{
                      width: '100%', padding: 'var(--space-2)', boxSizing: 'border-box',
                      border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--text-sm)', marginBottom: 'var(--space-2)',
                    }}
                  />
                  <button
                    onClick={createNewView}
                    disabled={createView.isPending}
                    style={{
                      width: '100%', padding: 'var(--space-2)',
                      background: 'var(--color-brand)', color: 'var(--color-text-inverse)',
                      border: 'none', borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--text-sm)', cursor: 'pointer',
                    }}
                  >
                    {t('save_view')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Divider */}
      <span style={{ width: 1, height: 20, background: 'var(--color-border)' }} />

      {/* Filters button */}
      <div ref={filterRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setShowFilters((v) => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
            padding: '4px var(--space-3)',
            background: activeCount > 0 ? 'var(--color-brand-pale)' : 'none',
            border: `1px solid ${activeCount > 0 ? 'var(--color-brand)' : 'var(--color-border)'}`,
            borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
            color: activeCount > 0 ? 'var(--color-brand)' : 'var(--color-text-secondary)',
            cursor: 'pointer',
          }}
        >
          <Filter size={13} />
          {t('filters.title')}
          {activeCount > 0 && (
            <span style={{
              background: 'var(--color-brand)', color: 'var(--color-text-inverse)',
              borderRadius: 'var(--radius-full)', fontSize: '10px',
              padding: '0 5px', lineHeight: '16px',
            }}>
              {activeCount}
            </span>
          )}
        </button>

        {showFilters && (
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 100 }}>
            <FilterPanel
              fields={fieldDefs}
              filters={localFilters}
              onChange={setLocalFilters}
            />
          </div>
        )}
      </div>

      {/* Columns button */}
      <div ref={columnRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setShowColumns((v) => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
            padding: '4px var(--space-3)', background: 'none',
            border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', cursor: 'pointer',
          }}
        >
          <Columns3 size={13} />
          {t('columns.toggle')}
        </button>

        {showColumns && (
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 100 }}>
            <ColumnPicker
              columns={columnDefs}
              visible={localColumns}
              onChange={setLocalColumns}
            />
          </div>
        )}
      </div>

      {/* Save button — shown when there's an active view with unsaved changes */}
      {activeViewId && isDirty && (
        <button
          onClick={saveCurrentView}
          disabled={updateActiveView.isPending}
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
            padding: '4px var(--space-3)', background: 'var(--color-brand)',
            border: 'none', borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-sm)', color: 'var(--color-text-inverse)', cursor: 'pointer',
          }}
        >
          <Save size={13} />
          {t('update_view')}
        </button>
      )}

      {/* Pin/share controls for active view */}
      {activeView && (
        <>
          <PinToggle view={activeView} />
          <ShareToggle view={activeView} />
        </>
      )}

      {/* Pinned view quick-access chips */}
      {pinnedViews.length > 0 && !activeView && pinnedViews.map((view) => (
        <button
          key={view.id}
          onClick={() => activateView(view)}
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
            padding: '2px var(--space-2)',
            background: 'var(--color-bg-subtle)', border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-full)', fontSize: 'var(--text-xs)',
            color: 'var(--color-text-secondary)', cursor: 'pointer',
          }}
        >
          <Pin size={10} />
          {view.name}
        </button>
      ))}
    </div>
  );

  // Unused until we need to call inside the component
  void togglePin;
}

// ── Sub-components ─────────────────────────────────────────────────────────

function ViewMenuItem({
  label, isActive, onClick,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', padding: 'var(--space-2) var(--space-3)',
        background: isActive ? 'var(--color-brand-pale)' : 'none',
        border: 'none', cursor: 'pointer', textAlign: 'left',
        fontSize: 'var(--text-sm)',
        color: isActive ? 'var(--color-brand)' : 'var(--color-text)',
        borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}
    >
      {label}
      {isActive && <Check size={12} />}
    </button>
  );
}

function PinToggle({ view }: { view: SavedView }) {
  const { t } = useTranslation('views');
  const update = useUpdateView(view.id);

  function toggle() {
    update.mutate({ isPinned: !view.isPinned });
  }

  return (
    <button
      onClick={toggle}
      title={view.isPinned ? t('unpin') : t('pin')}
      style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: view.isPinned ? 'var(--color-brand)' : 'var(--color-text-muted)',
        display: 'flex', padding: 'var(--space-1)',
      }}
    >
      {view.isPinned ? <Pin size={14} /> : <PinOff size={14} />}
    </button>
  );
}

function ShareToggle({ view }: { view: SavedView }) {
  const { t } = useTranslation('views');
  const update = useUpdateView(view.id);

  function toggle() {
    update.mutate({ isShared: !view.isShared });
  }

  return (
    <button
      onClick={toggle}
      title={view.isShared ? t('unshare') : t('share')}
      style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: view.isShared ? 'var(--color-brand)' : 'var(--color-text-muted)',
        display: 'flex', padding: 'var(--space-1)',
      }}
    >
      <Share2 size={14} />
    </button>
  );
}
